<!-- filepath: /Users/kianvos/Programmeren/stage_bureau_verduurzamen/maatregelstromen-intake/src/views/FormAanpakView.vue -->
<template>
  <interview-layout>
    <div>
      <h1 class="px-4 fade-in-up-enter-active animation-slowest max-100 text-primary">
        Isolatieaanpak
      </h1>
      <div class="d-flex flex-column flex-lg-row justify-content-center">
        <base-card
          class="mx-auto fade-in-up-enter-active animation-slowest delay-400"
          inner-class="container-narrow"
          color="alternate"
          :outer-padding="true"
        >
          <base-card class="mb-4" inner-class="small" color="secondary">
            <h6 class="mb-2">Uw situatie</h6>
            <checkmark-item class="mb-2" icon="info-circle-fill">
              Op basis van uw antwoorden hebben wij een keuze geselecteerd die volgens ons het beste bij uw situatie past.
            </checkmark-item>
            <checkmark-item class="mb-2" icon="info-circle-fill">
              U kunt deze keuze laten staan of een andere optie kiezen die beter bij u aansluit.
            </checkmark-item>
            <checkmark-item 
              class="mb-2" 
              icon="info-circle-fill"
              v-if="filteredSituationOptions.some(option => option.value === 'waiting_postcode')"
            >
              Uw postcode gaat open op: <strong>{{ lead.address.pc_m29_open_from_string || 'nog niet bekend' }}</strong>
            </checkmark-item>
          </base-card>

          <base-input
            type="radiobuttons"
            direction="column"
            name="DefaultUserSituation"
            v-model:value="lead.intakeCompletion.UserSituation"
            @update:value="handleUserSituationChange"
            :options="[defaultOption]"
            class="mb-3"
          >
            <template #default>
              <span class="custom-label text-success">Onze aanbevolen keuze:</span>
            </template>
          </base-input>

          <base-card
            v-if="lead.intakeCompletion.UserSituation === defaultUserSituation"
            class="mb-3"
            inner-class="small"
            color="info"
          >
            <checkmark-item icon="info-circle-fill">
              <span v-html="selectedSituationExplanation"></span>
            </checkmark-item>
          </base-card>

          <base-input
            v-if="otherOptions.length > 0"
            type="radiobuttons"
            direction="column"
            name="OtherUserSituations"
            v-model:value="lead.intakeCompletion.UserSituation"
            @update:value="handleUserSituationChange"
            :options="otherOptions"
          >
            <template #default>
              <span class="custom-label text-success">Andere beschikbare opties:</span>
            </template>
          </base-input>

          <base-card
            v-if="lead.intakeCompletion.UserSituation !== defaultUserSituation"
            class="mt-3"
            inner-class="small"
            color="info"
          >
            <checkmark-item icon="info-circle-fill">
              <span v-html="selectedSituationExplanation"></span>
            </checkmark-item>
          </base-card>
        </base-card>
      </div>

      <button-row class="mt-2 flex-row-reverse">
        <base-button
          class="ms-auto"
          icon="bi-arrow-right"
          @click="onSubmit"
          :disabled="v$.intakeCompletion.$invalid"
          >Volgende</base-button
        >
        <base-button
          variant="minimal-secondary"
          icon="bi-arrow-left"
          icon-position="start"
          @click="onPrev"
          >Terug</base-button
        >
      </button-row>
    </div>
  </interview-layout>
</template>

<script>
import { getDefaultUserSituation, getFilteredSituationOptions } from "@/services/UserSituation";
import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseButton from "@/components/BaseButton.vue";
import BaseInput from "@/components/BaseInput.vue";
import ButtonRow from "@/components/ButtonRow.vue";
import CheckmarkItem from "@/components/CheckmarkItem.vue";

import postcodeAppointmentConfig from "@/config/postcodeAppointmentConfig";

import { useAllStores } from "@/helpers/UseAllStores";
import { FlowService } from "@/services/FlowService";

export default {
  components: {
    InterviewLayout,
    BaseCard,
    BaseButton,
    BaseInput,
    ButtonRow,
    CheckmarkItem,
  },
  setup() {
    return { ...useAllStores() };
  },
  data() {
    return {
      situationOptions: [
        { label: "Ik wil nu een isolatieplan aanvragen", value: "appointment_isolatieplan" },
        { label: "Ik heb al een isolatieplan aangevraagd en wacht daarop", value: "waiting_isolatieplan" },
        { label: "Ik zoek ondersteuning bij een offerte of het uitvoeren van een maatregel.", value: "help_execution" },
        { label: "Ik ga een isolatieadviseur selecteren in het portaal van Nij Begun", value: "selecting_own_advisor" },
        { label: "Ik wil advies voor de volgende stap om direct te isoleren", value: "quotes_without_plan" },
        { label: "Ik wil wachten tot mijn postcodefasering open is", value: "waiting_postcode" },
        { label: "Ik wil alleen hulp met Terugwerkende kracht (maatregel29)", value: "help_retroactive" },
        { label: "Ik wil algemene uitleg over de Isolatieaanpak, maar (nog) niet verduurzamen", value: "general_info" },

        { label: "Ik wil hulp met het financieren van de maatregelen", value: "help_financing" },
        { label: "Ik heb een probleem bij de uitvoering en wil hier ondersteuning bij hebben", value: "help_finishing" },
      ],
      yesNo: [
        { label: "Ja", value: true },
        { label: "Nee", value: false },
      ],
    };
  },
  computed: {
    filteredSituationOptions() {
      return getFilteredSituationOptions(this.lead, this.postcodeOpen, this.situationOptions);
    },
    postcodeOpen() {
      const dateStr = this.lead.address.pc_m29_open_from_datetime;
      if (!dateStr) return false;
      const openDate = new Date(String(dateStr).replace(" ", "T"));
      if (Number.isNaN(openDate.getTime())) return false;

      const now = new Date();
      const limit = new Date(
        now.getTime() +
          (Number(postcodeAppointmentConfig.daysUntilShowScheduler) || 0) *
            24 *
            60 *
            60 *
            1000
      );
      return openDate <= limit;
    },
    selectedSituationExplanation() {
      const explanations = {
        appointment_isolatieplan: "De volgende stap voor u is een afspraak in te plannen met een bewonersbegeleider. Op de volgende pagina kunt u direct een afspraak maken.",
        waiting_isolatieplan: "De volgende stap voor u is om, zodra u uw plan heeft ontvangen, dit door te sturen naar <a href='mailto:klantenservice@bureauverduurzamen.nl'>klantenservice@bureauverduurzamen.nl</a>.",
        // quotes_without_plan: "De volgende stap voor u is om een offerte aan te vragen bij een uitvoerende partij. Ga hiervoor naar deze pagina: <a href='https://verduurzamingsbedrijven.nl/aangesloten-partijen-zoeken/' target='_blank'>https://verduurzamingsbedrijven.nl/</a>.",
        quotes_without_plan: "Zowel met een isolatieplan als een offerte kunt u subsidie aanvragen. U doet dit voordat u de aannemer opdracht geeft. Zo heeft u altijd zekerheid dat u subsidie krijgt.",
        waiting_postcode: "De volgende stap voor u is even geduld te hebben. Een maand voordat uw postcode open gaat, nemen wij contact met u op om het isolatieplan aan te vragen.",
        help_retroactive: "De volgende stap voor u is dat wij contact met u opnemen om uw situatie verder te bespreken.",
        general_info: "De volgende stap voor u is dat wij contact met u opnemen om de werkwijze van de verduurzamingsaanpak toe te lichten.",
        help_execution: "De volgende stap voor u is om uw isolatieplan door te sturen naar <a href='mailto:klantenservice@bureauverduurzamen.nl'>klantenservice@bureauverduurzamen.nl</a>.",

        help_financing: "De volgende stap voor u is dat wij contact met u opnemen om uw situatie verder te bespreken.",
        help_finishing: "De volgende stap voor u is dat wij contact met u opnemen",
        selecting_own_advisor: "De volgende stap voor u is een isolatieadviseur te selecteren in het portaal van Nij Begun."
      };

      return explanations[this.lead.intakeCompletion.UserSituation] || "Er wordt binnen een aantal werkdagen contact met u opgenomen voor de volgende stappen.";
    },
    defaultUserSituation() {
      return getDefaultUserSituation(this.lead, this.postcodeOpen);
    },
    defaultOption() {
      // Haal de default optie uit de lijst
      return this.situationOptions.find((option) => option.value === this.defaultUserSituation);
    },
    otherOptions() {
      // Filter de overige opties (exclusief de default)
      return this.filteredSituationOptions.filter((option) => option.value !== this.defaultUserSituation);
    },
  },
  methods: {
    touchAndValidate() {
      this.v$.intakeCompletion.$touch();
      return !this.v$.intakeCompletion.$invalid;
    },
    onSubmit() {
      if (this.touchAndValidate()) {
        FlowService.pushNext(this);
      }
    },
    onPrev() {
      FlowService.pushPrev(this);
    },
    handleUserSituationChange(value) {
      if (value === this.defaultUserSituation) {
        this.lead.intakeCompletion.UserSituationManuallySet = false;
      } else {
        this.lead.intakeCompletion.UserSituationManuallySet = true;
      }

      // if (value !== 'appointment_isolatieplan' && value !== 'waiting_postcode') {
      //   this.lead.meta.LeadStatus = 'Bel afspraak';
      // } else {
      //   this.lead.meta.LeadStatus = null;
      // }

      this.lead.updatePostcodeCallbackDate();
    },
    handleWillingToSpendChange(value) {
      if (value === true) {
        this.lead.intakeCompletion.UserSituation = "appointment_isolatieplan";
      } else {
        this.lead.intakeCompletion.UserSituation = "waiting_postcode";
      }

      this.lead.intakeCompletion.UserSituationManuallySet = false;
    },
  },
  mounted() {
    const currentUserSituation = this.lead.intakeCompletion.UserSituation;
    const isCurrentSituationValid = this.filteredSituationOptions.some(
      (option) => option.value === currentUserSituation
    );

    if (!isCurrentSituationValid) {
      this.lead.intakeCompletion.UserSituationManuallySet = false;
      this.lead.intakeCompletion.UserSituation = this.defaultUserSituation;
      this.lead.updatePostcodeCallbackDate();
      return;
    }

    if (this.lead.intakeCompletion.UserSituationManuallySet) {
      return;
    }

    this.lead.intakeCompletion.UserSituation = this.defaultUserSituation;   
    this.lead.updatePostcodeCallbackDate();
  },
};
</script>

<style scoped lang="scss">

.issue-summary-button-wrapper {
  font-size: 0.8em;
  text-align: center;
}
.overview-item {
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 1 !important;
  }
}

.custom-label {
  font-size: 1.2em;
  font-weight: bold; 
  color: #28a745;
}

.responsive-intake-end-header {
  @media (max-width: 768px) {
    flex-direction: column !important;
    align-items: stretch !important;
    > div,
    > .base-card {
      width: 100%;
      margin-bottom: 1.5rem;
    }
  }
}
</style>
